import { Button } from "@/components/ui/Button";
import { DialogFooter } from "@/components/ui/Dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { useCreatePaymentChannel } from "@/hooks/services/use-payment-channel";
import {
  adminPaymentChannelFormSchema,
  type adminPaymentChannelFormValues,
} from "@/schemas/payment-channels";
import { useDialogStore } from "@/store/dialog-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Minus, Plus } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

const CreateAdminPaymentChannelForm = () => {
  const createMutation = useCreatePaymentChannel();
  const { closeCreateDialog } = useDialogStore();
  const form = useForm<adminPaymentChannelFormValues>({
    resolver: zodResolver(adminPaymentChannelFormSchema),
    defaultValues: {
      name: "",
      description: "",
      keyCheck: [],
      active: true,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "keyCheck" as never,
  });

  const onSubmit = async (values: adminPaymentChannelFormValues) => {
    console.log("values", values);

    try {
      await createMutation.mutateAsync({
        nonce: "string",
        payload: values,
      });

      toast.success("Create payment channel successful!");
    } catch (error) {
      console.error("Failed to create payment channel:", error);
    }

    closeCreateDialog();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter value" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Enter value" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <FormLabel>Key Check Parameters</FormLabel>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append("")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Parameter
            </Button>
          </div>

          {fields.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No parameters added. Click 'Add Parameter' to start adding key
              check parameters.
            </p>
          )}

          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-2">
                <FormField
                  control={form.control}
                  name={`keyCheck.${index}`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          placeholder={`Parameter ${index + 1}`}
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) =>
                            form.setValue(`keyCheck.${index}`, e.target.value)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => remove(index)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Active Status</FormLabel>
                <FormDescription>
                  Enable or disable this payment channel
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Channel
              </>
            ) : (
              "Create Channel"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default CreateAdminPaymentChannelForm;
